import {
  buildChildrenIndex,
  buildParentIdMap,
  getAncestorIds,
  getDescendantIds,
} from './helpers';
import type { TreeNodeDefinition } from './types';

/**
 * Toggle one node in a cascading checkbox selection: descendants follow the
 * toggled node; ancestors are selected only when every descendant is selected.
 */
export const toggleCascadingTreeSelection = <T>(
  nodes: readonly TreeNodeDefinition<T>[],
  selectedIds: ReadonlySet<string>,
  toggledId: string
): ReadonlySet<string> => {
  const parentIdMap = buildParentIdMap([...nodes]);
  if (!parentIdMap.has(toggledId)) {
    return selectedIds;
  }

  const childrenIndex = buildChildrenIndex([...nodes]);
  const descendantIds = getDescendantIds(toggledId, childrenIndex);
  const ancestorIds = getAncestorIds(toggledId, parentIdMap);
  const next = new Set(selectedIds);

  if (selectedIds.has(toggledId)) {
    next.delete(toggledId);
    for (const id of descendantIds) {
      next.delete(id);
    }
    for (const id of ancestorIds) {
      next.delete(id);
    }
    return next;
  }

  next.add(toggledId);
  for (const id of descendantIds) {
    next.add(id);
  }
  for (const ancestorId of ancestorIds) {
    const allChecked = getDescendantIds(ancestorId, childrenIndex).every((id) =>
      next.has(id)
    );
    if (allChecked) {
      next.add(ancestorId);
    }
  }
  return next;
};
